// Графік роботи закладу: компанія пише його текстом («Пн-Ср 11:00–22:00, Пт-Сб 11:00–00:00, Нд вихідний»),
// а застосунку потрібні години по днях, щоб показати «Відчинено / Скоро зачиниться / Зачинено».
// Копія lib/schedule.js з бекенду (там розбирається графік для застосунку) — міняти обидва.

// Дні: 1 = понеділок … 7 = неділя
const DAY_PREFIXES = [
  [1, ['пн', 'пон', 'mo']],
  [2, ['вт', 'вів', 'tu']],
  [3, ['ср', 'сер', 'we']],
  [4, ['чт', 'чет', 'th']],
  [5, ['пт', 'пят', 'п’ят', "п'ят", 'fr']],
  [6, ['сб', 'суб', 'sa']],
  [7, ['нд', 'нед', 'вс', 'вос', 'su']],
];
const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];
const EVERY_DAY = ['щодня', 'щоденно', 'щоднини', 'ежедневно', 'daily', 'everyday', 'кожен', 'кожного', 'без'];
const ROUND_CLOCK = ['цілодобово', 'круглосуточно', 'нонстоп'];
const CLOSED = ['вихідний', 'выходной', 'closed', 'зачинено', 'закрито', 'закрыто', 'не'];
const RANGE_WORDS = ['до', 'по', 'to', 'till', 'until'];

function dayOf(word) {
  for (const [day, prefixes] of DAY_PREFIXES) {
    if (prefixes.some((p) => word === p || (p.length >= 3 && word.startsWith(p)))) return day;
  }
  // Повні англійські назви: monday, tuesday …
  const en = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].findIndex((d) => d.startsWith(word) && word.length >= 3);
  return en >= 0 ? en + 1 : null;
}

function minutesOf(token) {
  const m = /^(\d{1,2})(?:[:.](\d{2}))?$/.exec(token);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2] || 0);
  if (h > 24 || min > 59 || (h === 24 && min > 0)) return null;
  return h * 60 + min;
}

const pad = (n) => String(n).padStart(2, '0');
const hhmm = (minutes) => `${pad(Math.floor((minutes % 1440) / 60))}:${pad(minutes % 60)}`;

/**
 * Розбирає текст графіка. Повертає [{ day, open: 'HH:MM', close: 'HH:MM' }] (дні без годин — вихідні;
 * close менше або дорівнює open — заклад працює після опівночі), або null, якщо графік не вдалося зрозуміти.
 */
export function parseSchedule(text) {
  if (typeof text !== 'string' || !text.trim()) return null;
  const source = text.toLowerCase().replace(/24\s*\/\s*7/g, ' цілодобово ');
  const tokens = source.match(/\d{1,2}(?:[:.]\d{2})?|[\p{L}'’]+|[-‐-―−]/gu) || [];

  const week = new Map(); // day -> [[open, close]]
  const closed = new Set();
  let pending = []; // дні, до яких належать наступні години
  let lastDays = null; // дні попереднього проміжку (для «9:00-13:00, 14:00-18:00»)
  let explicitDays = false;
  let rangeFrom = null; // початок діапазону днів «Пн-Пт»
  let ranges = 0;

  const addRange = (days, open, close) => {
    for (const day of days) {
      if (!week.has(day)) week.set(day, []);
      week.get(day).push([open, close]);
      closed.delete(day);
    }
    ranges += 1;
  };

  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    const isDash = /^[-‐-―−]$/.test(token);

    const open = minutesOf(token);
    if (open !== null) {
      // Час: «11:00 – 22:00», «з 11 до 22»
      let j = i + 1;
      if (tokens[j] && (/^[-‐-―−]$/.test(tokens[j]) || RANGE_WORDS.includes(tokens[j]))) j += 1;
      const close = j > i + 1 && tokens[j] !== undefined ? minutesOf(tokens[j]) : null;
      if (close === null) continue;
      const days = pending.length ? pending : lastDays || (explicitDays ? [] : ALL_DAYS);
      if (days.length) addRange(days, open, close);
      lastDays = days;
      pending = [];
      i = j;
      continue;
    }
    if (isDash || RANGE_WORDS.includes(token)) {
      if (pending.length) rangeFrom = pending[pending.length - 1];
      continue;
    }

    const day = dayOf(token);
    if (day) {
      explicitDays = true;
      lastDays = null;
      if (rangeFrom !== null) {
        // «Пт-Нд», «Сб-Пн» — по колу через неділю
        for (let d = rangeFrom % 7 + 1; ; d = d % 7 + 1) {
          if (!pending.includes(d)) pending.push(d);
          if (d === day) break;
        }
        rangeFrom = null;
      } else {
        pending.push(day);
      }
      continue;
    }
    rangeFrom = null;

    if (EVERY_DAY.some((w) => token.startsWith(w))) {
      pending = [...ALL_DAYS];
      lastDays = null;
    } else if (ROUND_CLOCK.some((w) => token.startsWith(w))) {
      addRange(pending.length ? pending : explicitDays ? [] : ALL_DAYS, 0, 1440);
      lastDays = null;
      pending = [];
    } else if (CLOSED.some((w) => token === w || (w.length > 3 && token.startsWith(w)))) {
      for (const d of pending.length ? pending : lastDays || []) {
        closed.add(d);
        week.delete(d);
      }
      if (pending.length || lastDays) explicitDays = true;
      pending = [];
      lastDays = null;
    }
  }

  if (!ranges) return null;
  const result = [];
  for (const day of ALL_DAYS) {
    for (const [open, close] of (week.get(day) || []).sort((a, b) => a[0] - b[0])) {
      result.push({ day, open: hhmm(open), close: close === 1440 && open === 0 ? '24:00' : hhmm(close) });
    }
  }
  return result.length ? result : null;
}
